import { useState } from "react";
import styled from "styled-components";
import { BASIC_DARK_GRAY, BASIC_LIGHT_GRAY } from "../../../constants/colors";
import {
  OrderCoupon,
  OrderMenu,
  OrderStatus,
  TaxDetail,
} from "../../../ducks/order";
import { Button } from "../../atoms/Button";
import { OrderHistoryBadge } from "../../molecules/OrderHistoryBadge";
import { OrderContent } from "./OrderContent";

type Props = {
  storeName: string;
  storeAddress: string;
  orderId: string;
  pickupDateText: string;
  menus: OrderMenu[];
  orderPriceWithTax: number;
  orderQuantity: number;
  orderStatus: OrderStatus;
  isAdvancedPayment: boolean;
  canReorder: boolean;
  tax: number;
  taxDetails?: TaxDetail[];
  memo?: string;
  coupon?: OrderCoupon;
  onCancel: () => void;
  onReOrder: () => void;
  hideCancel?: boolean;
};

export const OrderHistoryItem: React.FC<Props> = ({
  storeName,
  storeAddress,
  orderId,
  pickupDateText,
  orderStatus,
  orderPriceWithTax,
  orderQuantity,
  isAdvancedPayment,
  canReorder,
  tax,
  taxDetails,
  memo,
  coupon,
  menus,
  onCancel,
  onReOrder,
  hideCancel,
}) => {
  const [isDetailVisible, setDetailVisible] = useState(false);

  const toggleDetailVisibility = () => {
    setDetailVisible((prev) => !prev);
  };

  console.log(orderId, hideCancel);

  return (
    <OrderHistoryContainer>
      <OrderHistoryHeader>
        <Row>
          <Column>
            <StoreName>{storeName}</StoreName>
            <StoreAddress>{storeAddress}</StoreAddress>
          </Column>
          <Column>
            <OrderHistoryBadge status={orderStatus} />
          </Column>
        </Row>
        <Row>
          <Column>
            <OrderId>
              <span>注文ID : </span>
              <span data-wovn-ignore>{orderId}</span>
            </OrderId>
            {isAdvancedPayment && <PaymentBadge>事前決済済み</PaymentBadge>}
            <Text>お受け取り日時</Text>
            <PickupDate>{pickupDateText}</PickupDate>
          </Column>
          <Column>
            {!hideCancel && orderStatus === OrderStatus.New && (
              <ButtonContainer>
                <Button
                  btnStyle="secondary"
                  size="xxs"
                  text="キャンセル"
                  onClick={onCancel}
                  rounded="sm"
                />
              </ButtonContainer>
            )}
          </Column>
        </Row>
      </OrderHistoryHeader>

      <OrderDetail>
        {isDetailVisible && (
          <OrderDetailWrapper>
            <OrderContent
              title="購入した商品"
              menus={menus}
              totalQuantity={orderQuantity}
              totalPrice={orderPriceWithTax}
              taxAmount={tax}
              taxDetails={taxDetails}
              coupon={coupon}
              hideMemo
            />

            {memo && (
              <MemoContainer>
                <p>メモ</p>
                <div data-wovn-ignore>{memo}</div>
              </MemoContainer>
            )}

            {canReorder && (
              <ButtonPreOrder>
                <Button
                  text="同じものを注文する"
                  btnStyle="primary"
                  onClick={onReOrder}
                  rounded="md"
                />
              </ButtonPreOrder>
            )}
          </OrderDetailWrapper>
        )}

        <DropdownBtn
          className={isDetailVisible ? "show" : ""}
          onClick={toggleDetailVisibility}
        />
      </OrderDetail>
    </OrderHistoryContainer>
  );
};

const OrderHistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  padding: 25px 16px 14px;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  background-color: ${BASIC_LIGHT_GRAY};
  margin-bottom: 10px;
`;

const OrderHistoryHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const StoreName = styled.p`
  font-size: 20px;
  line-height: 1;
  font-weight: 600;
  margin: 0;
`;

const StoreAddress = styled.p`
  font-size: 14px;
  font-weight: 600;
`;

const OrderId = styled.p`
  font-size: 14px;
  font-weight: 600;
`;

const Text = styled.p`
  font-size: 14px;
  font-weight: 600;
`;

const PickupDate = styled.p`
  font-size: 14px;
  font-weight: 600;
`;

const ButtonContainer = styled.div`
  button {
    font-size: 16px;
    padding: 11px 13px 9px;
    width: auto;
  }
`;

const OrderDetail = styled.div`
  padding-bottom: 15px;
`;

const OrderDetailWrapper = styled.div`
  margin: 14px 0 0 0;
`;

const MemoContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px 0;
  gap: 10px;
  p {
    font-weight: 600;
  }
  div {
    font-weight: 200;
  }
`;

const ButtonPreOrder = styled.div`
  margin: 20px 0;
  display: flex;
  justify-content: center;
  button {
    line-height: 1;
    padding: 20px 0 18px 0;
    height: auto;
    font-weight: 600;
  }
`;

const DropdownBtn = styled.div`
  cursor: pointer;
  width: 1px;
  border-right: 20px solid transparent;
  border-bottom: 10px solid ${BASIC_DARK_GRAY};
  border-left: 20px solid transparent;
  margin: 0 auto;
  transform: rotate(180deg);
  transition: all 0.5s ease;
  &.show {
    transform: rotate(0deg);
  }
`;

const PaymentBadge = styled.div`
  border: 1px solid ${BASIC_DARK_GRAY};
  font-weight: 600;
  width: fit-content;
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 4px;
  margin: 5px 0;
`;
